import { bindable, autoinject, observable, computedFrom } from "aurelia-framework";
import val from "core/val";
import { WorkTaskModel } from "api/models/company/work-task-model";
import { WorkOrderWorkTaskDetailsModel } from "api/models/company/workorder/work-order-work-task-details-model";
import { ListDocumentModel } from "api/models/company/document/list-document-model";
import { I18N } from "aurelia-i18n";
import { WorkOrderWorkTaskService } from "services/work-order-work-task-service";
import {DailyEntryWorkTaskService } from "services/daily-entry-work-task-service";
import { RouterHelper } from "helpers/router-helper";
import { WorkOrderWorkTaskDocumentService } from "services/work-order-work-task-document-service";
import { DailyEntryWorkTaskDocumentService } from "services/daily-entry-work-task-document-service";
import { EventAggregator } from "aurelia-event-aggregator";
import { WorkTaskService } from "services/work-task-service";
import { WorkTaskDailyEntryService } from "services/work-task-daily-entry-service";
import { RouteInfo } from "./ma-work-task-index";
import notificationHelper from "helpers/notificationHelper";
import Parse from "helpers/parse";
import { CreationSource } from "api/enums/creation-source";
import { CloneHelper } from "helpers/cloneHelper";

@autoinject
export class MaWorkTaskEdit {
    public val: any = val;
    public option: any = "";
    public workTask!: WorkOrderWorkTaskDetailsModel;
    public unModifiedWorkTask: any | null = null;

    public selectedCode: any;
    public isCodeSelected: boolean = false;
    public isNewWorkTask: boolean = true;
    public isFromMaestro: boolean = false;

    public deleteAction!: (id: any) => Promise<boolean>;

    @observable public isCompleted: boolean = false;
    @observable public isDiffered: boolean = false;

    public subscription: any;
    public pictures: ListDocumentModel[] = [];

    @bindable public dispatchProjectCode: string = "";
    @bindable public dispatchDate: any;
    @bindable public transactionNumber?: number;

    @bindable public newWorkTask!: () => Promise<WorkOrderWorkTaskDetailsModel>;
    @bindable public documentRouteInfo!: RouteInfo;
    @bindable public isDirty: boolean = false;
    @bindable public id: any;
    public readonly: boolean = false;

    @computedFrom("workTask.TaskCode", "workTask.TaskDescription", "workTask.Note", "workTask.IsCompleted", "workTask.IsDiffered", "workTask.DateRequired", "workTask.DateCompleted", "workTask.Comment", "unModifiedWorkTask")
    get isDirtyCheck(): boolean {
        if (this.readonly) {
            this.isDirty = false;
            return this.isDirty;
        }

        if (!this.unModifiedWorkTask) {
            this.isDirty = false;
            return this.isDirty;
        }

        const stringifyUnmodified = JSON.stringify(this.unModifiedWorkTask).replace(/[^0-9A-Z]+/gi, "");
        const stringifyCurrent = JSON.stringify(this.workTask).replace(/[^0-9A-Z]+/gi, "");

        this.isDirty = stringifyUnmodified !== stringifyCurrent;

        return this.isDirty;
    }

    constructor(private readonly i18n: I18N, private readonly workTaskService: WorkTaskService, private readonly workTaskDailyEntryService: WorkTaskDailyEntryService, private readonly workOrderWorkTaskService: WorkOrderWorkTaskService, private readonly dailyEntryWorkTaskService: DailyEntryWorkTaskService, private readonly routerHelper: RouterHelper, private readonly workOrderWorkTaskDocumentService: WorkOrderWorkTaskDocumentService, private readonly dailyEntryWorkTaskDocumentService: DailyEntryWorkTaskDocumentService, private readonly eventAggregator: EventAggregator) {}

    public async bind(): Promise<void> {
        this.subscription = this.eventAggregator.subscribe("document-upload-finish", () => this.reloadDocuments());

        const transactionNumber = this.transactionNumber ? this.transactionNumber : 0;
        this.isNewWorkTask = !(transactionNumber > 0);

        this.readonly = Parse.Boolean(this.documentRouteInfo.routeParams.readonly);

        if (this.isNewWorkTask) {
           this.workTask = (await this.newWorkTask())!;
        } else {
            this.workTask = !this.dispatchProjectCode ? (await this.workOrderWorkTaskService.GetWorkTask(transactionNumber))! : (await this.dailyEntryWorkTaskService.GetWorkTask(transactionNumber))!;
            this.isFromMaestro = this.workTask.CreationSource === CreationSource.Maestro;

            this.initialiseSelection(this.workTask);
        }

        this.deleteAction = !this.dispatchProjectCode ? this.workOrderWorkTaskDocumentService.DeleteDocument.bind(this.workOrderWorkTaskDocumentService, transactionNumber) : this.dailyEntryWorkTaskDocumentService.DeleteDocument.bind(this.dailyEntryWorkTaskDocumentService, transactionNumber);

        this.unModifiedWorkTask = CloneHelper.deepClone(this.workTask);
    }

    public async reloadDocuments(): Promise<void> {
        this.pictures = !this.dispatchProjectCode ? (await this.workOrderWorkTaskDocumentService.GetDocumentsForWorkTask(this.workTask.TransactionNumber, "1"))! : (await this.dailyEntryWorkTaskDocumentService.GetDocumentsForWorkTask(this.workTask.TransactionNumber, "1"))!;
    }

    public get GetCodes(): any {
        return {
            transport: (params: any, success: any, failure: any): any => {
               !this.dispatchProjectCode ? this.workTaskService.GetTasks(params.data.filter, { page: params.data.page, pageSize: 20 }).then(
                    (result: WorkTaskModel[]) => {
                        return success(result);
                    },
                    (fail: any) => {
                        return failure(fail);
                    }
                ) : this.workTaskDailyEntryService.GetTasks(params.data.filter, { page: params.data.page, pageSize: 20 }).then(
                    (result: WorkTaskModel[]) => {
                        return success(result);
                    },
                    (fail: any) => {
                        return failure(fail);
                    }
                );
            },
            mapResults: (item: WorkTaskModel): any => {
                return { id: item.Code, text: `${item.Code} - ${item.Description}`, data: item };
            }
        };
    }

    public onCodeSelected(workTask: WorkTaskModel): void {
        this.workTask.TaskCode = workTask.Code;
        this.workTask.TaskDescription = workTask.Description;
        this.workTask.Note = workTask.Note;

        this.isCodeSelected = true;
    }

    public onCodeCleared(): void {
        this.workTask.TaskCode = null;
        this.workTask.TaskDescription = null;
        this.workTask.Note = null;

        this.isCodeSelected = false;
    }

    public isCompletedChanged(newValue: boolean | null, oldValue: boolean | null): void {
        if (newValue !== null && newValue) {
                this.isDiffered = false;
        }

        this.setInfo();
    }

    public isDifferedChanged(newValue: boolean | null, oldValue: boolean | null): void {
        if (newValue !== null && newValue) {
            this.isCompleted = false;
        }

        this.setInfo();
    }

    public async save(): Promise<void> {
        if (this.readonly) {
            return;
        }

        if (!this.workTask.TaskDescription) {
            const dialogMessage = `${this.i18n.tr("ApiError.617")}`;
            notificationHelper.showError(dialogMessage, "", { timeOut: 0, extendedTimeOut: 0 });
            return;
        }

        this.setInfo();

        !this.dispatchProjectCode ? await this.workOrderWorkTaskService.SaveWorkTask(this.workTask) : await this.dailyEntryWorkTaskService.SaveWorkTask(this.dispatchProjectCode, this.dispatchDate, this.workTask);
        this.unModifiedWorkTask = null;
        this.routerHelper.navigateBack();
    }

    public setInfo(): void {
        if (this.workTask) {
            this.workTask.IsCompleted = this.isCompleted;
            this.workTask.IsDiffered = this.isDiffered;

            if (!this.isCompleted) {
                this.workTask.DateCompleted = null;
            }

            if (this.isCompleted && this.dispatchProjectCode) {
                this.workTask.DateCompleted = this.dispatchDate;
            }
        }
    }

    public async addPicture(): Promise<void> {
        if (this.readonly) {
            return;
        }

        this.unModifiedWorkTask = null;
        !this.dispatchProjectCode ? await this.workOrderWorkTaskService.SaveWorkTask(this.workTask) : await this.dailyEntryWorkTaskService.SaveWorkTask(this.dispatchProjectCode, this.dispatchDate, this.workTask);
        this.routerHelper.navigateToRoute(this.documentRouteInfo.routeName, this.documentRouteInfo.routeParams);
    }

    private async initialiseSelection(workTask: WorkOrderWorkTaskDetailsModel): Promise<void> {
        if (workTask.TaskCode) {
            this.selectedCode =  { id: workTask.TaskCode, text: `${workTask.TaskCode} - ${workTask.TaskDescription}`};
            this.isCodeSelected = true;
        }

        this.isCompleted = this.workTask.IsCompleted;
        this.isDiffered = this.workTask.IsDiffered;

        this.reloadDocuments();
    }
}
